import { fetchAuthSession } from 'aws-amplify/auth';
import toast from 'react-hot-toast';
import awsExports from '../aws-exports';

const API_ENDPOINT = awsExports.aws_cloud_logic_custom?.[0]?.endpoint || '';

// Background calls that legitimately fail on stale data (IAP receipt re-delivery,
// passive sync) MUST go through silentFetch — using request() would surface a
// toast for every failure on every app launch.
export async function silentFetch(path, method = 'GET', body = null) {
  try {
    const authHeaders = await getAuthHeaders();
    const options = {
      method,
      headers: { 'Content-Type': 'application/json', ...authHeaders },
    };
    if (body) options.body = JSON.stringify(body);
    const res = await fetch(`${API_ENDPOINT}${path}`, options);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

async function getAuthHeaders() {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (token) {
      return { Authorization: token };
    }
  } catch {
    // Not authenticated
  }
  return {};
}

async function request(path, options = {}) {
  const authHeaders = await getAuthHeaders();
  const url = `${API_ENDPOINT}${path}`;

  let response;
  try {
    response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers,
      },
    });
  } catch (networkErr) {
    toast.error('Network error — check your connection');
    throw networkErr;
  }

  if (response.status === 401) {
    // Token expired or revoked — let caller handle (typically redirect to login)
    const err = new Error('Unauthorized');
    err.status = 401;
    throw err;
  }

  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: response.statusText }));
    const message = data.error || `Request failed (${response.status})`;
    toast.error(message);
    const err = new Error(message);
    err.status = response.status;
    err.data = data;
    throw err;
  }

  return response.json();
}

export const api = {
  // Decks
  getDecks: () => request('/decks'),
  getDeck: (deckId) => request(`/decks/${deckId}`),
  createDeck: (data) => request('/decks', { method: 'POST', body: JSON.stringify(data) }),
  updateDeck: (deckId, data) => request(`/decks/${deckId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteDeck: (deckId) => request(`/decks/${deckId}`, { method: 'DELETE' }),

  // Cards
  getCards: (deckId) => request(`/decks/${deckId}/cards`),
  getDueCards: (deckId) => request(`/decks/${deckId}/due`),
  createCard: (deckId, data) => request(`/decks/${deckId}/cards`, { method: 'POST', body: JSON.stringify(data) }),
  updateCard: (cardId, deckId, data) => request(`/cards/${cardId}?deckId=${deckId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCard: (cardId, deckId) => request(`/cards/${cardId}?deckId=${deckId}`, { method: 'DELETE' }),

  // Sync
  syncReviews: (reviews) => request('/sync/reviews', { method: 'POST', body: JSON.stringify({ reviews }) }),

  // Stats
  getStats: () => request('/stats'),
  recordStats: (data) => request('/stats', { method: 'POST', body: JSON.stringify(data) }),

  // Account
  deleteAccount: () => request('/account', { method: 'DELETE' }),

  // Subscription
  getSubscription: () => request('/subscription'),
  verifyAppleTransaction: (data) => request('/subscription/apple-verify', { method: 'POST', body: JSON.stringify(data) }),

  // AI deck generation. generateDeck is user-initiated → toast path;
  // polling uses silentFetch so a transient poll blip never toasts.
  generateDeck: (data) => request('/decks/generate', { method: 'POST', body: JSON.stringify(data) }),
  getGenerationJob: (jobId) => silentFetch(`/decks/generate/${jobId}`),
};
